import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PhoneInput10 from "@/components/site/PhoneInput10";
import { toE164, isValid10Digit } from "@/lib/phone";
import { ShieldStar, Warning, CheckCircle, Phone } from "@phosphor-icons/react";

/* ============================================================================
   AcceptInvite — public page where invitees claim their COE seat.
   ----------------------------------------------------------------------------
   Flow:
     1. Page loads with `:token` from URL, GETs invite metadata (name, role, ...)
     2. Invitee confirms their phone number
     3. POST /api/coe/invites/accept → user record created with the role/permissions
        baked into the invite
     4. Invitee redirected to /admin-login to sign in via OTP using the same phone
   ========================================================================== */

export default function AcceptInvite() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [invite, setInvite] = useState(null);
    const [error, setError] = useState("");
    const [phone, setPhone] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        api.get(`/coe/invites/${token}`)
            .then((r) => setInvite(r.data))
            .catch((e) => setError(e.response?.data?.detail || "This invite is invalid or has been used."));
    }, [token]);

    const accept = async () => {
        if (!isValid10Digit(phone)) {
            toast.error("Enter your 10-digit mobile number.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/coe/invites/accept", { token, phone: toE164(phone) });
            setDone(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not accept invite.");
        }
        setSubmitting(false);
    };

    if (error) {
        return (
            <div className="min-h-screen grid place-items-center px-4">
                <div className="max-w-md w-full bg-card border border-border rounded-2xl p-6 text-center">
                    <Warning size={28} weight="fill" className="mx-auto text-rose-500 mb-2" />
                    <h1 className="font-display text-xl font-bold mb-1">Invite unavailable</h1>
                    <p className="text-sm text-muted-foreground">{error}</p>
                    <Link to="/" className="inline-block mt-4 text-sm text-primary hover:underline">Go home</Link>
                </div>
            </div>
        );
    }

    if (!invite) {
        return <div className="min-h-screen grid place-items-center px-4 text-muted-foreground">Loading invite…</div>;
    }

    if (done) {
        return (
            <div className="min-h-screen grid place-items-center px-4" data-testid="invite-accepted">
                <div className="max-w-md w-full bg-card border border-border rounded-2xl p-6 text-center">
                    <CheckCircle size={36} weight="fill" className="mx-auto text-emerald-500 mb-2" />
                    <h1 className="font-display text-2xl font-bold mb-1">Welcome, {invite.name}.</h1>
                    <p className="text-sm text-muted-foreground mb-4">
                        Your <span className="font-semibold">{invite.role_label}</span> seat is ready. Sign in with your phone via OTP to start working.
                    </p>
                    <Button onClick={() => navigate("/admin-login")} className="rounded-full" data-testid="invite-go-login">
                        Continue to sign-in
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen grid place-items-center px-4 py-12" data-testid="accept-invite-page">
            <div className="max-w-md w-full bg-card border border-border rounded-2xl p-6 sm:p-8">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Invisique™ COE</div>
                <h1 className="font-display text-3xl font-bold tracking-tight mt-0.5">You're invited.</h1>
                <p className="text-sm text-muted-foreground mt-2">
                    <span className="font-semibold">{invite.created_by_name || "An admin"}</span> has invited you to join the COE team as a{" "}
                    <span className="inline-flex items-center gap-1 font-semibold text-primary">
                        <ShieldStar size={12} weight="fill" /> {invite.role_label}
                    </span>.
                </p>
                {invite.note && (
                    <p className="text-xs italic text-muted-foreground bg-secondary/40 rounded-lg p-3 mt-3 border-l-2 border-primary">
                        "{invite.note}"
                    </p>
                )}

                <div className="mt-6 space-y-3">
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Confirm your phone</Label>
                        <div className="mt-1.5">
                            <PhoneInput10
                                value={phone}
                                onChange={setPhone}
                                inputClassName="h-11"
                                testid="invite-phone"
                            />
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1">
                            You'll sign in with this number using a one-time code each time.
                        </p>
                    </div>
                    <Button onClick={accept} disabled={submitting} className="w-full h-11 rounded-full" data-testid="invite-accept-btn">
                        {submitting ? "Joining…" : "Accept and join COE team"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
